import React from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import {
  LeadSourceType,
  type WebsiteContactFormFields
} from '~/@types/graphql-police'
import { submitContactUsForm } from '~/api/contact-forms'
import FormSelect from '~/components/Form/FormSelect'
import FormTextField from '~/components/Form/FormTextField'
import Link from '~/components/Link'
import LoadingButton from '~/components/LoadingButton'
import Typography from '~/components/Typography'
import { GCLID_STORAGE_KEY } from '~/constants/env'
import { ROUTES } from '~/constants/routes'
import { useNavigate } from '~/hooks/useNavigate'
import { trackWebmarketerEvent } from '~/third-parties/webmarketer'
import { trackEvent } from '~/utils/tracking'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import {
  contactUsFormSchema,
  type ContactUsFormValues
} from './ContactUsForm.constants'

export type ContactUsFormProps = {
  isBrochureRequested: boolean
}

const ContactUsForm = ({ isBrochureRequested }: ContactUsFormProps) => {
  const { t } = useTranslation(['common', 'contact'])
  const navigate = useNavigate()
  const [messageError, setMessageError] = React.useState('')

  const formMethods = useForm({
    resolver: zodResolver(contactUsFormSchema)
  })

  const submitContactUsFormMutation = useMutation({
    mutationFn: async ({ fields }: { fields: WebsiteContactFormFields }) => {
      const result = await submitContactUsForm(fields)

      if (result !== true) {
        return Promise.reject(t('common:errors.unknown'))
      }

      return result
    },
    onError: (error: unknown) => {
      setMessageError(
        typeof error === 'string' ? error : t('common:errors.unknown')
      )
    },
    onSuccess: (_, { fields }) => {
      trackEvent('SEND_CONTACT_US_FORM')
      trackWebmarketerEvent({
        eventType: 'contact-private',
        data: {
          firstname: fields.firstName,
          lastname: fields.lastName,
          email: fields.email,
          phone: fields.phone,
          gclid: fields.gclid,
          'lead-source': fields.leadSource
        }
      })
      navigate(ROUTES.contactUs_confirm, {
        replace: true
      })
    }
  })

  const handleSubmit = (values: ContactUsFormValues) => {
    if (submitContactUsFormMutation.isPending) {
      return
    }

    const fields: WebsiteContactFormFields = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      message: values.message,
      leadSource: values.leadSource,
      isBrochureRequested
    }

    if (values.company) {
      fields.company = values.company
    }

    if (values.phone) {
      fields.phone = values.phone
    }

    const gclidFromStorage = localStorage.getItem(GCLID_STORAGE_KEY)

    if (gclidFromStorage) {
      fields.gclid = gclidFromStorage
    }

    if (values.leadSource === LeadSourceType.Other) {
      fields.leadSourceComment = values.leadSourceComment
    }

    submitContactUsFormMutation.mutate({
      fields
    })
  }

  return (
    <form
      className="flex flex-col gap-y-12"
      noValidate
      onSubmit={formMethods.handleSubmit(handleSubmit)}
    >
      <div className="flex flex-col gap-y-10">
        <div className="flex gap-10 max-md:flex-col">
          <div className="flex-1">
            <FormTextField
              name="firstName"
              control={formMethods.control}
              placeholder="Paul"
              label={t('common:firstName')}
              fullWidth
              required
            />
          </div>
          <div className="flex-1">
            <FormTextField
              name="lastName"
              control={formMethods.control}
              placeholder="Dupont"
              label={t('common:lastName')}
              fullWidth
              required
            />
          </div>
        </div>
        <FormTextField
          name="company"
          control={formMethods.control}
          placeholder={t('common:myCompany')}
          type="text"
          label={t('common:company')}
          fullWidth
        />
        <FormTextField
          name="email"
          control={formMethods.control}
          placeholder="paul@dupont.com"
          type="email"
          label={t('common:email')}
          fullWidth
          required
        />
        <FormTextField
          name="phone"
          control={formMethods.control}
          placeholder="06 00 00 00 00"
          type="text"
          label={t('common:phone')}
          fullWidth
        />
        <FormSelect
          name="leadSource"
          fullWidth
          control={formMethods.control}
          label={t('contact:howDidYouHearAboutUs')}
          required
          placeholder={t('common:selectAnAnswer')}
        >
          <option value={LeadSourceType.RecommendationOfARelative}>
            {t('contact:leadSource_recommendationOfARelative')}
          </option>
          <option value={LeadSourceType.Doctor}>
            {t('contact:leadSource_doctor')}
          </option>
          <option value={LeadSourceType.SocialNetworks}>
            {t('contact:leadSource_socialNetworks')}
          </option>
          <option value={LeadSourceType.Press}>
            {t('contact:leadSource_press')}
          </option>
          <option value={LeadSourceType.SearchEngine}>
            {t('contact:leadSource_searchEngine')}
          </option>
          <option value={LeadSourceType.Podcast}>
            {t('contact:leadSource_podcast')}
          </option>
          <option value={LeadSourceType.Event}>
            {t('contact:leadSource_event')}
          </option>
          <option value={LeadSourceType.Other}>
            {t('contact:leadSource_other')}
          </option>
        </FormSelect>
        {formMethods.watch('leadSource') === LeadSourceType.Other ? (
          <FormTextField
            className="-mt-7"
            name="leadSourceComment"
            control={formMethods.control}
            placeholder={t('common:pleaseSpecify')}
          />
        ) : null}
        <FormTextField
          name="message"
          control={formMethods.control}
          placeholder={t('common:specifyYourRequest')}
          label={t('common:message')}
          fullWidth
          rows={4}
          required
          isTextarea
        />
      </div>
      <LoadingButton
        isLoading={submitContactUsFormMutation.isPending}
        type="submit"
        size="lg"
        rounded="full"
        color="primary"
        fullWidth
        testId="contactUs"
      >
        {submitContactUsFormMutation.isPending
          ? t('common:sending')
          : t('common:send')}
      </LoadingButton>
      {messageError ? (
        <Typography align="center" className="text-error">
          {messageError}
        </Typography>
      ) : null}
      <Typography variant="mention" className="text-black/80">
        <Trans i18nKey="common:collectFormData">
          <Link
            testId="privacyNotice"
            to={ROUTES.privacyNotice}
            underline="always"
            target="_blank"
          />
        </Trans>
      </Typography>
    </form>
  )
}

export default ContactUsForm
